import { CommonModule } from '@angular/common';
/* eslint-disable @typescript-eslint/no-empty-function */
import {
  ChangeDetectorRef,
  Component,
  ContentChild,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  DropdownPosition,
  NgSelectModule,
} from '@ng-select/ng-select';
import { TranslateModule } from '@ngx-translate/core';
import { iif, Observable, of, Subject, Subscription } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  startWith,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import { NgSelectLabelDirective } from './directives/ng-select-label-tmp.directive';
import { NgSelectMultiLabelDirective } from './directives/ng-select-multi-label-tmp.directive';
import { NgSelectOptionDirective } from './directives/ng-select-option-tmp.directive';

@Component({
  selector: 'app-ng-select-typeahead',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NgSelectModule,
    TranslateModule,
  ],
  templateUrl: './ng-select-typeahead.component.html',
  styleUrls: ['./ng-select-typeahead.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: NgSelectTypeaheadComponent,
    },
  ],
})
export class NgSelectTypeaheadComponent<Data, Response = Data[]>
  implements OnChanges, OnInit, OnDestroy, ControlValueAccessor
{
  @Input() bindLabel = 'label';
  @Input() bindValue!: string;
  @Input() clearable = true;
  @Input() closeOnSelect = false;
  @Input() clearChoicesOnLessMinTerm = true;
  @Input() clearSearchOnAdd = false;
  @Input() dropdown: Array<Data> = [];
  @Input() dropdownPosition?: DropdownPosition;
  @Input() loading = false;
  @Input() minTermLength = 2;
  @Input() multiple = false;
  @Input() ngSelectClass?: string;
  @Input() placeholder!: string;
  @Input() appendTo = '';
  @Input() addTag = false;
  @Input() addTagText!: string;
  /**
   * Set input to read only mode.
   * The `true` value is preferred when it is used with `setDisabledState`.
   */
  @Input() readonly = false;
  @Input() invalid = false;
  @Input() selectedValue!: Data;
  @Input() showAllAsDefault = false;
  @Input() startWith?: Array<Data> | null;
  @Input() typeToSearchText =
    'COMMON.Please enter 2 or more characters';
  @Input() notFoundText = 'SELECT.No items found';
  @Output() selectedValueChange = new EventEmitter<Data>();
  @Output() dropdownClose = new EventEmitter();
  @ContentChild(NgSelectLabelDirective)
  labelContent!: NgSelectLabelDirective;
  @ContentChild(NgSelectMultiLabelDirective)
  multiLabelContent!: NgSelectMultiLabelDirective;
  @ContentChild(NgSelectOptionDirective)
  optionContent!: NgSelectOptionDirective;

  dropdown$!: Observable<Data[]>;
  formControl = new FormControl();
  search$ = new Subject<string>();
  startWith$ = new Subject<typeof this.startWith>();
  touched = false;

  private compSubs: Subscription = new Subscription();
  /**
   * The disable state of the input which come from `setDisabledState`.
   */
  private disabled = false;
  private startWithSubs = new Subscription();

  @Input() accessor: (res: Response) => Data[] = (res) =>
    res as unknown as Data[];
  /** A function that access data from response to return array of data */
  @Input() dataApiGenerator: (
    search: string,
  ) => Observable<Response> = (search) =>
    of([{ label: search, value: search }] as unknown as Response);

  private _onTouched = () => {};

  // eslint-disable-next-line @typescript-eslint/member-ordering
  constructor(private cdRef: ChangeDetectorRef) {
    const subs = this.startWith$.subscribe({
      next: (res) => {
        this.dropdown = res || [];
      },
    });
    this.startWithSubs.add(subs);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['startWith']) {
      this.startWith$.next(changes['startWith'].currentValue);
    }
  }

  ngOnInit(): void {
    this.formControl.setValue(this.selectedValue, {
      emitEvent: false,
    });
    const search$ = this.search$.pipe(
      distinctUntilChanged(),
      debounceTime(200),
    );
    let xhrSubs = new Subscription();
    this.dropdown$ = iif(
      () => this.showAllAsDefault,
      search$.pipe(startWith('')),
      search$.pipe(
        filter(
          (text): text is string =>
            text != null &&
            (this.clearChoicesOnLessMinTerm || text !== ''),
        ),
      ),
    ).pipe(
      tap(() => (this.loading = true)),
      switchMap((text) => {
        if (!xhrSubs.closed) {
          xhrSubs.unsubscribe();
        }
        if (this.clearChoicesOnLessMinTerm) {
          if (this.isLessThanMinTermLength(text)) return of([]);
        }
        const subject$ = new Subject<Response>();
        xhrSubs = this.dataApiGenerator(text).subscribe(subject$);
        return subject$.pipe(map(this.accessor));
      }),
    );
    const dropdownSubs = this.dropdown$.subscribe({
      next: (res) => {
        this.loading = false;
        if (!Array.isArray(res)) {
          throw new Error(
            'Data response has not been array. Use the accessor to convert type',
          );
        }
        this.dropdown = res;
        this.cdRef.markForCheck();
      },
      error: (err) => {
        this.loading = false;
        console.error(err);
      },
    });
    this.compSubs.add(dropdownSubs);
  }

  ngOnDestroy(): void {
    this.compSubs.unsubscribe();
    this.startWithSubs.unsubscribe();
  }

  clearDropdown(): void {
    this.dropdown = [];
  }

  isLessThanMinTermLength(searchText: string): boolean {
    return (
      searchText == null || searchText.length < this.minTermLength
    );
  }

  markAsTouched(): void {
    if (!this.touched) {
      this._onTouched();
      this.touched = true;
    }
  }

  onClose(): void {
    this.dropdownClose.emit();
    if (this.clearChoicesOnLessMinTerm) {
      this.dropdown = [];
    }
  }

  onOpen(): void {
    if (!this.showAllAsDefault) {
      return;
    }
    if (this.selectedValue == null && this.dropdown.length === 0) {
      this.dataApiGenerator('')
        .pipe(take(1), map(this.accessor))
        .subscribe({
          next: (res) => (this.dropdown = res),
        });
    }
  }

  onSearchChange(event: { term: string; items: Response[] }) {
    if (
      this.clearChoicesOnLessMinTerm &&
      this.isLessThanMinTermLength(event.term)
    ) {
      this.search$.next(event.term);
    }
  }

  onSelect(selectedData: Data): void {
    this.selectedValue = selectedData;
    this.markAsTouched();
    this.selectedValueChange.emit(this.selectedValue);
  }

  registerOnChange(onChangeFn: any): void {
    const onChangeSub =
      this.formControl.valueChanges.subscribe(onChangeFn);
    this.compSubs.add(onChangeSub);
  }

  registerOnTouched(onTouchFn: any): void {
    this._onTouched = onTouchFn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  writeValue(data: Data): void {
    if (!this.dropdown.length) {
      if (data && typeof data === 'object') {
        if ((data as { [k: string]: any })[this.bindLabel]) {
          this.dropdown = [data];
        }
      }
    }
    this.formControl.setValue(data, { emitEvent: false });
    // TODO: Need implement
    this.selectedValue = data;
  }

  get _readonly(): boolean {
    return this.readonly || this.disabled;
  }
}
